import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { telegramClientApi } from '@/shared';
import { SeasonInfoResponseType } from '@/entities';

export interface SeasonInfoState {
  data: SeasonInfoResponseType | null;
  loading: boolean;
  error: string | null;
}

const initialState: SeasonInfoState = {
  data: null,
  loading: false,
  error: null
};

export const fetchSeasonInfo = createAsyncThunk<
  SeasonInfoResponseType,
  void,
  { rejectValue: string }
>('season/fetchSeasonInfo', async (_, { rejectWithValue }) => {
  try {
    const res = await telegramClientApi.getSeasonInfo();

    if (res.error === null && res.data) {
      return res.data;
    } else if (res.error !== null) {
      return rejectWithValue(res.error.message);
    }

    return rejectWithValue('Unknown error occurred');
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

const seasonInfoSlice = createSlice({
  name: 'seasonInfo',
  initialState,
  reducers: {
    clearSeasonInfoData: (state) => {
      state.data = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSeasonInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSeasonInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchSeasonInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch season info';
      });
  }
});

export const { clearSeasonInfoData } = seasonInfoSlice.actions;

export default seasonInfoSlice.reducer;
